import type { components } from '../../../../packages/api-client/src/generated/schema';

import { browserApiClient, requiredApiData } from './browser-api';
import { plannedBuildingCapabilities } from '../settlement-goals';

export type BuildRequest = components['schemas']['BuildRequest'];
export type BuildSettlement = components['schemas']['SettlementResponse'];
export interface BuildOption {
  code: string;
  label: string;
  cost: Record<string, number>;
  durationSeconds: number;
  width: number;
  height: number;
  maxLevel: number;
  opportunities: string[];
}

function record(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

export function parseBuildOption(item: components['schemas']['BuildingCatalogItem']): BuildOption {
  const config = record(item.config);
  const first = record(record(config.levels)['1']);
  const cost = record(first.cost);
  const footprint = config.footprint;
  if (
    Object.keys(cost).length === 0 ||
    Object.values(cost).some((amount) => !Number.isSafeInteger(amount) || Number(amount) < 0) ||
    !Array.isArray(footprint) ||
    footprint.length !== 2 ||
    footprint.some((size) => !Number.isInteger(size) || size < 1 || size > 100) ||
    typeof first.duration_seconds !== 'number' ||
    first.duration_seconds < 0
  )
    throw new Error(`Construction details for ${item.code} are unavailable. Please retry.`);
  return {
    code: item.code,
    label:
      typeof config.display_name === 'string'
        ? config.display_name
        : item.code.replaceAll('_', ' '),
    cost: cost as Record<string, number>,
    durationSeconds: first.duration_seconds,
    width: Number(footprint[0]),
    height: Number(footprint[1]),
    maxLevel: item.max_level,
    opportunities: plannedBuildingCapabilities(config, 1),
  };
}

export async function loadBuildCatalog(signal?: AbortSignal) {
  const client = browserApiClient();
  const [catalogResponse, settlementResponse, economyResponse] = await Promise.all([
    client.GET('/api/v1/settlement/buildings/catalog', { signal }),
    client.GET('/api/v1/settlement', { signal }),
    client.GET('/api/v1/economy/summary', { signal }),
  ]);
  const catalog = requiredApiData(
    catalogResponse.data,
    catalogResponse.error,
    'Building catalog is unavailable.',
  );
  const settlement = requiredApiData(
    settlementResponse.data,
    settlementResponse.error,
    'Your settlement is unavailable.',
  );
  const economy = requiredApiData(
    economyResponse.data,
    economyResponse.error,
    'Resource balances are unavailable.',
  );
  return {
    options: catalog.buildings.map(parseBuildOption),
    settlement,
    balances: economy.balances,
  };
}

// Placement suggestions are presentation only. The server rechecks the entire footprint at spend time.
export function availableBuildPlots(
  option: BuildOption,
  settlement: BuildSettlement,
  districtCode: string,
  rotation: number,
) {
  const district = settlement.districts.find(
    (item) => item.code === districtCode && item.unlocked_at,
  );
  if (!district) return [];
  const width = rotation % 2 ? option.height : option.width;
  const height = rotation % 2 ? option.width : option.height;
  const plots: Array<{ x: number; y: number }> = [];
  for (
    let y = Math.max(-50, district.min_y);
    y <= Math.min(50, district.max_y - height + 1);
    y += 1
  ) {
    for (
      let x = Math.max(-50, district.min_x);
      x <= Math.min(50, district.max_x - width + 1);
      x += 1
    ) {
      if (
        !settlement.buildings.some(
          (building) =>
            x < building.x + building.footprint_width &&
            x + width > building.x &&
            y < building.y + building.footprint_height &&
            y + height > building.y,
        )
      )
        plots.push({ x, y });
    }
  }
  return plots;
}

export async function createSettlementBuilding(body: BuildRequest, idempotencyKey: string) {
  if (!navigator.onLine) throw new Error('Reconnect before starting construction.');
  const response = await browserApiClient({ mutation: true }).POST('/api/v1/settlement/buildings', {
    params: { header: { 'Idempotency-Key': idempotencyKey } },
    body,
  });
  return requiredApiData(response.data, response.error, 'Construction could not start.');
}

export async function unlockSettlementDistrict(districtCode: string, idempotencyKey: string) {
  if (!navigator.onLine) throw new Error('Reconnect before expanding your settlement.');
  const response = await browserApiClient({ mutation: true }).POST(
    '/api/v1/settlement/districts/{district_code}/unlock',
    {
      params: {
        path: { district_code: districtCode },
        header: { 'Idempotency-Key': idempotencyKey },
      },
    },
  );
  return requiredApiData(response.data, response.error, 'This district could not be unlocked.');
}
